/**
 * Visibility helpers shared by the onboarding overlay and spotlight.
 *
 * A plain getBoundingClientRect() size test is NOT enough. A collapsed sidebar
 * group keeps its child links mounted (grid-rows-[0fr] + opacity-0), and
 * getBoundingClientRect() ignores ancestor clipping/opacity — so a hidden child
 * still reports a non-zero box, laid out directly under the group toggle (i.e.
 * overlapping the next visible nav row). That made the spotlight/presence logic
 * treat a collapsed child as "present" and point at the wrong element.
 *
 * isElementVisible() additionally rejects anything hidden by display,
 * visibility, or opacity:0 on the element OR any ancestor.
 */
export function isElementVisible(el: HTMLElement | null): el is HTMLElement {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;

  // Modern single-call check: catches display:none / visibility:hidden /
  // opacity:0 on the element or any ancestor. Option names differ across
  // engine versions, so pass both the new and legacy spellings (unknown keys
  // are ignored per spec).
  const cv = (el as unknown as {
    checkVisibility?: (opts?: Record<string, boolean>) => boolean;
  }).checkVisibility;
  if (typeof cv === 'function') {
    return cv.call(el, {
      opacity: true,
      visibilityProperty: true,
      contentVisibilityAuto: true,
      checkOpacity: true,
      checkVisibilityCSS: true,
    });
  }

  // Fallback for engines without checkVisibility: walk ancestors.
  let node: HTMLElement | null = el;
  while (node) {
    const s = getComputedStyle(node);
    if (s.display === 'none' || s.visibility === 'hidden' || parseFloat(s.opacity) === 0) {
      return false;
    }
    node = node.parentElement;
  }
  return true;
}

// First genuinely-visible match for a selector (a selector can match several
// nodes, e.g. a responsive field rendered twice with one copy hidden).
export function pickVisibleEl(selector: string): HTMLElement | null {
  const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
  for (const el of els) {
    if (isElementVisible(el)) return el;
  }
  return null;
}
